import React, { useMemo } from 'react';

/*
 * The spine of the page: every theme as a bar across the year, grouped into
 * domain swimlanes and coloured by the hat it mostly demanded. Bar length is
 * the span the work ran over; the trailing number is how many pieces of work
 * rolled into it.
 *
 * Layout note: each domain gets its own header slot above its rows, so group
 * labels and theme labels never share a horizontal band.
 */

const W = 1000;
const GUTTER = 278; // left label column -- fits the longest theme title
const RIGHT = 46; // room for the trailing weight number
const AXIS = 46; // month axis band
const ROW = 26;
const GROUP_HEAD = 26; // header slot above each domain's rows
const GROUP_GAP = 16; // breathing room after a domain
const MIN_BAR = 14;
const MAX_LABEL = 38;

const ms = (iso) => Date.parse(`${iso}T00:00:00Z`);

const monthTicks = (startIso, endIso) => {
  const out = [];
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  let y = start.getUTCFullYear();
  let m = start.getUTCMonth();
  while (y < end.getUTCFullYear() || (y === end.getUTCFullYear() && m <= end.getUTCMonth())) {
    out.push(new Date(Date.UTC(y, m, 1)));
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
};

const truncate = (text) =>
  text.length > MAX_LABEL ? `${text.slice(0, MAX_LABEL - 1).trimEnd()}…` : text;

const ThemeTimeline = ({ themes, domains, spanStart, spanEnd, activeHat, selected, onSelect }) => {
  const { rows, height, x, ticks } = useMemo(() => {
    const t0 = ms(spanStart);
    const t1 = ms(spanEnd);
    const scale = (iso) => GUTTER + ((ms(iso) - t0) / (t1 - t0)) * (W - GUTTER - RIGHT);

    const built = [];
    let y = AXIS;
    for (const domain of domains) {
      const members = themes.filter((theme) => theme.domain === domain.name);
      if (!members.length) continue;
      built.push({ kind: 'group', name: domain.name, weight: domain.weight, y: y + 17 });
      y += GROUP_HEAD;
      for (const theme of members) {
        built.push({ kind: 'theme', theme, y: y + ROW / 2 });
        y += ROW;
      }
      y += GROUP_GAP;
    }
    return {
      rows: built,
      height: y - GROUP_GAP + 14,
      x: scale,
      ticks: monthTicks(spanStart, spanEnd),
    };
  }, [themes, domains, spanStart, spanEnd]);

  const dimmed = (theme) => activeHat && !theme.hats.includes(activeHat);

  return (
    <div className="theme-timeline">
      <svg
        viewBox={`0 0 ${W} ${height}`}
        role="img"
        aria-label={`Timeline of ${themes.length} themes across ${ticks.length} months`}
      >
        {/* month grid + axis */}
        {ticks.map((tick) => {
          const iso = tick.toISOString().slice(0, 10);
          const tx = x(iso);
          const isJanuary = tick.getUTCMonth() === 0;
          return (
            <g key={iso}>
              <line
                className={`tl-grid${isJanuary ? ' is-year' : ''}`}
                x1={tx}
                y1={AXIS - 12}
                x2={tx}
                y2={height - 6}
              />
              <text className={`tl-tick${isJanuary ? ' is-year' : ''}`} x={tx} y={AXIS - 22} textAnchor="middle">
                {tick.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}
              </text>
              {isJanuary && (
                <text className="tl-year" x={tx} y={AXIS - 34} textAnchor="middle">
                  {tick.getUTCFullYear()}
                </text>
              )}
            </g>
          );
        })}

        {rows.map((row) => {
          if (row.kind === 'group') {
            return (
              <text key={`g-${row.name}`} className="tl-group" x={0} y={row.y}>
                {row.name}
                <tspan className="tl-group-weight" dx="8">{row.weight}</tspan>
              </text>
            );
          }

          const { theme, y } = row;
          const x1 = x(theme.start);
          const x2 = Math.max(x(theme.end), x1 + MIN_BAR);
          const isSelected = selected && selected.title === theme.title;
          const current = theme.status === 'current';
          return (
            <g
              key={theme.title}
              className={`tl-row${dimmed(theme) ? ' is-dimmed' : ''}${isSelected ? ' is-selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={`${theme.title}, ${theme.domain}, ${theme.weight} pieces of work`}
              onClick={() => onSelect(theme)}
              onFocus={() => onSelect(theme)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(theme);
                }
              }}
            >
              <rect className="tl-hit" x={0} y={y - ROW / 2} width={W} height={ROW} rx={4} />
              <text className="tl-label" x={GUTTER - 16} y={y} textAnchor="end" dominantBaseline="middle">
                {truncate(theme.title)}
              </text>
              <rect
                className={`tl-bar${current ? ' is-current' : ''}`}
                x={x1}
                y={y - 7}
                width={x2 - x1}
                height={14}
                rx={7}
                style={{ '--bar': `var(--tmc-hat-${theme.hats[0]})` }}
              />
              <text className="tl-weight" x={x2 + 9} y={y} dominantBaseline="middle">
                {theme.weight}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ThemeTimeline;
